import { PhotoType } from "@/utils/types"

import { dexie } from "../../db"
import { checkIfIndexedDbIsSupported } from "../../utils/checkIfIndexedDbIsSupported"
import { logDexieError } from "../../utils/logDexieError"

// ─── Types ─────────────────────────────────────────────────────────────────────

export type VideoItem = {
  dexieId: number
  id: string
  sessionId: string

  type: PhotoType
  arrayBuffer?: ArrayBuffer
  mimeType?: string

  uploaded: 0 | 1
  readyToUpload?: 0 | 1

  url?: string
  thumbnailUrl?: string

  // Upload state (replaces Jotai pending state)
  uploadProgress: number // -1 = waiting, 0-100 = uploading, 101 = done
  uploadFailed: boolean
  skipped?: boolean
}

// ─── Methods ───────────────────────────────────────────────────────────────────

export async function upsertVideo(data: Omit<VideoItem, "dexieId">): Promise<void> {
  try {
    await dexie.transaction("rw", dexie.videos, async () => {
      const existing = await dexie.videos.where("id").equals(data.id).first()
      if (existing) {
        await dexie.videos.update(existing.dexieId, data)
      } else {
        await dexie.videos.add(data)
      }
    })
  } catch (error) {
    logDexieError(error)
  }
}

export async function findAllVideosForSession(sessionId: string): Promise<VideoItem[]> {
  try {
    return await dexie.videos.where("sessionId").equals(sessionId).toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function getVideosByStep(sessionId: string, type: PhotoType): Promise<VideoItem[]> {
  try {
    return await dexie.videos
      .where("sessionId")
      .equals(sessionId)
      .and((video) => video.type === type)
      .toArray()
  } catch (error) {
    logDexieError(error)
    return []
  }
}

export async function getPendingVideos() {
  const isIndexedDbSupported = await checkIfIndexedDbIsSupported()
  if (!isIndexedDbSupported) return

  try {
    const videos = await dexie.videos
      .where("uploaded")
      .equals(0)
      .and((video) => video.readyToUpload === 1)
      .toArray()
    return videos
  } catch (error) {
    logDexieError(error)
  }
}

export async function addVideo(data: Omit<VideoItem, "dexieId">) {
  const isIndexedDbSupported = await checkIfIndexedDbIsSupported()
  if (!isIndexedDbSupported) return

  try {
    await dexie.videos.add(data)
  } catch (error) {
    logDexieError(error)
  }
}

export async function markVideoAsReadyToUpload(videoId: string) {
  const isIndexedDbSupported = await checkIfIndexedDbIsSupported()
  if (!isIndexedDbSupported) return

  try {
    const video = await dexie.videos.where("id").equals(videoId).first()
    if (!video) return

    if (video.readyToUpload === 1) return

    dexie.videos.update(video, { readyToUpload: 1 })
  } catch (error) {
    logDexieError(error)
  }
}

export async function deleteVideo(videoId: string) {
  const isIndexedDbSupported = await checkIfIndexedDbIsSupported()
  if (!isIndexedDbSupported) return

  try {
    const video = await dexie.videos.where("id").equals(videoId).first()
    if (!video) return

    await dexie.videos.delete(video.dexieId)
  } catch (error) {
    logDexieError(error)
  }
}

export async function getVideosByStepType({ type, sessionId }: { type: PhotoType; sessionId: string }) {
  const isIndexedDbSupported = await checkIfIndexedDbIsSupported()
  if (!isIndexedDbSupported) return

  try {
    return await dexie.videos
      .where("sessionId")
      .equals(sessionId)
      .and((video) => video.type === type)
      .toArray()
  } catch (error) {
    logDexieError(error)
  }
}

export async function markVideoAsUploaded(videoId: string) {
  const isIndexedDbSupported = await checkIfIndexedDbIsSupported()
  if (!isIndexedDbSupported) return

  try {
    const video = await dexie.videos.where("id").equals(videoId).first()
    if (!video) return

    await dexie.videos.update(video, { uploaded: 1 })
  } catch (error) {
    logDexieError(error)
  }
}
